import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Service, BookingFormData } from '../types';
import { getServices } from '../services/supabase';
import { AuthContext } from '../App';
import Calendar from '../components/Calendar';
import TimeSlotSelector from '../components/TimeSlotSelector';
import './BookingPage.css';


// --- Realistic Distance Calculation Function ---
// Uses Google Maps Distance Matrix API for real miles calculation
import { fetchDistanceFromApi } from '../utils/distanceApi';
import { fetchAvailability, AvailabilityBooking } from '../utils/availability';

const getDistance = async (
  fromAddress: string,
  toAddress: { address: string; city: string; state: string }
): Promise<number> => {
  const fullToAddress = `${toAddress.address}, ${toAddress.city}, ${toAddress.state}`;
  console.log(`Calculating distance from ${fromAddress} to ${fullToAddress}...`);
  try {
    const miles = await fetchDistanceFromApi(fromAddress, fullToAddress);
    return miles;
  } catch (err) {
    console.warn('Distance API failed, falling back to 20 miles. Error:', err);
    return 20; // fallback if API fails
  }
};


const getTravelFee = (distanceInMiles: number): number => {
  const freeMiles = 15;
  const feePerMile = 5;
  const maxFee = 40; // new cap per requirements
  if (distanceInMiles <= freeMiles) return 0;
  const milesOver = Math.max(0, Math.ceil(distanceInMiles - freeMiles));
  const fee = milesOver * feePerMile;
  return Math.min(fee, maxFee);
};

const MAX_BOOKING_ADVANCE_DAYS = 14;
const APPOINTMENT_BUFFER_MINUTES = 30;
const BUSINESS_OPEN_HOUR = 9;
const BUSINESS_CLOSE_HOUR = 18;
const BASE_SLOT_TIMES = ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'];

interface TimeSlotAvailability {
  time: string;
  available: boolean;
}

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDateTime = (dateStr: string, timeStr: string) => new Date(`${dateStr}T${timeStr}:00`);
const addMinutes = (date: Date, minutes: number) => new Date(date.getTime() + minutes * 60000);

// --- Main Component ---
const BookingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [travelFee, setTravelFee] = useState(0);
  const [travelMiles, setTravelMiles] = useState(0);
  const { profile } = useContext(AuthContext);

  const [availabilityMap, setAvailabilityMap] = useState<Record<string, AvailabilityBooking[]>>({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlotAvailability[]>([]);
  const [fullyBookedDates, setFullyBookedDates] = useState<string[]>([]);
  const [selectedDateBookings, setSelectedDateBookings] = useState<AvailabilityBooking[]>([]);

  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const maxSelectableDate = useMemo(() => {
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + MAX_BOOKING_ADVANCE_DAYS);
    return maxDate;
  }, [today]);

  const [formData, setFormData] = useState<BookingFormData>({
    serviceId: searchParams.get('service') || '',
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    scheduledDate: '',
    scheduledTime: '',
    notes: '',
    locationType: 'dropoff',
    address: '',
    city: '',
    state: ''
  });

  const businessAddress = '7230 Jemmah Lane, Jacksonville, Florida';

  // Fetch services from Supabase on component mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await getServices();
        setServices(servicesData);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
    // auth handled at App level via AuthContext
  }, []);

  useEffect(() => {
    const loadAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError(null);
      try {
        const start = formatDate(today);
        const end = formatDate(maxSelectableDate);
        const { bookings } = await fetchAvailability(start, end);

        const grouped = bookings.reduce<Record<string, AvailabilityBooking[]>>((acc, booking) => {
          if (!acc[booking.date]) {
            acc[booking.date] = [];
          }
          acc[booking.date].push(booking);
          return acc;
        }, {});

        // Ensure bookings within each day are sorted chronologically
        Object.keys(grouped).forEach(dateKey => {
          grouped[dateKey] = grouped[dateKey].sort((a, b) => a.time.localeCompare(b.time));
        });

        setAvailabilityMap(grouped);
      } catch (error) {
        console.error('Failed to fetch availability:', error);
        setAvailabilityError(error instanceof Error ? error.message : 'Failed to load availability.');
        setAvailabilityMap({});
      } finally {
        setAvailabilityLoading(false);
      }
    };

    loadAvailability();
  }, [today, maxSelectableDate]);

  // Recalculate travel fee whenever address or location type changes
  useEffect(() => {
    const updateTravelFee = async () => {
      if (formData.locationType === 'mobile' && formData.address && formData.city && formData.state) {
        const distance = await getDistance(businessAddress, {
          address: formData.address,
          city: formData.city,
          state: formData.state
        });
        const fee = getTravelFee(distance);
        setTravelMiles(distance);
        setTravelFee(fee);
      } else {
        setTravelFee(0);
        setTravelMiles(0);
      }
    };
    updateTravelFee();
  }, [formData.locationType, formData.address, formData.city, formData.state]);
  const selectedService = services.find(s => s.id === formData.serviceId);
  const bookedDateList = useMemo(() => Object.keys(availabilityMap), [availabilityMap]);

  const computeSlotsForDate = useCallback((dateStr: string): TimeSlotAvailability[] => {
    if (!selectedService) {
      return BASE_SLOT_TIMES.map(time => ({ time, available: false }));
    }

    const serviceDuration = selectedService.duration || 60;
    const bookingsForDate = availabilityMap[dateStr] || [];
    const dayOpens = toDateTime(dateStr, `${String(BUSINESS_OPEN_HOUR).padStart(2, '0')}:00`);
    const dayCloses = toDateTime(dateStr, `${String(BUSINESS_CLOSE_HOUR).padStart(2, '0')}:00`);

    return BASE_SLOT_TIMES.map(time => {
      const slotStart = toDateTime(dateStr, time);
      const slotEnd = addMinutes(slotStart, serviceDuration);
      const slotEndWithBuffer = addMinutes(slotEnd, APPOINTMENT_BUFFER_MINUTES);

      let available = true;

      if (slotStart < dayOpens) {
        available = false;
      }

      if (slotEndWithBuffer > dayCloses) {
        available = false;
      }

      if (available) {
        for (const booking of bookingsForDate) {
          const bookingDuration = booking.duration || serviceDuration;
          const bookingStart = toDateTime(dateStr, booking.time);
          const bookingEnd = addMinutes(bookingStart, bookingDuration);
          const bookingEndWithBuffer = addMinutes(bookingEnd, APPOINTMENT_BUFFER_MINUTES);

          const overlaps = slotStart < bookingEndWithBuffer && slotEndWithBuffer > bookingStart;
          if (overlaps) {
            available = false;
            break;
          }
        }
      }

      return { time, available };
    });
  }, [availabilityMap, selectedService]);

  useEffect(() => {
    if (!selectedService) {
      setAvailableSlots(BASE_SLOT_TIMES.map(time => ({ time, available: false })));
      setSelectedDateBookings([]);
      if (formData.scheduledTime) {
        setFormData(prev => ({ ...prev, scheduledTime: '' }));
      }
      return;
    }

    if (!formData.scheduledDate) {
      setAvailableSlots(BASE_SLOT_TIMES.map(time => ({ time, available: true })));
      setSelectedDateBookings([]);
      if (formData.scheduledTime) {
        setFormData(prev => ({ ...prev, scheduledTime: '' }));
      }
      return;
    }

    const slots = computeSlotsForDate(formData.scheduledDate);
    setAvailableSlots(slots);
    const bookingsForDate = availabilityMap[formData.scheduledDate] || [];
    setSelectedDateBookings(bookingsForDate);

    if (formData.scheduledTime) {
      const stillAvailable = slots.find(slot => slot.time === formData.scheduledTime)?.available;
      if (!stillAvailable) {
        setFormData(prev => ({ ...prev, scheduledTime: '' }));
      }
    }
  }, [selectedService, formData.scheduledDate, formData.scheduledTime, availabilityMap, computeSlotsForDate]);

  useEffect(() => {
    if (!selectedService) {
      setFullyBookedDates([]);
      return;
    }

    const dates: string[] = [];
    const iterator = new Date(today);

    while (iterator <= maxSelectableDate) {
      dates.push(formatDate(iterator));
      iterator.setDate(iterator.getDate() + 1);
    }

    const fullyBooked = dates.filter(dateStr => {
      const slots = computeSlotsForDate(dateStr);
      return slots.every(slot => !slot.available);
    });

    setFullyBookedDates(fullyBooked);
  }, [availabilityMap, selectedService, today, maxSelectableDate, computeSlotsForDate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateSelect = (date: string) => {
    setFormData(prev => ({ ...prev, scheduledDate: date }));
  };

  const handleTimeSelect = (time: string) => {
    setFormData(prev => ({ ...prev, scheduledTime: time }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Book Appointment button clicked');

    if (!selectedService) {
      alert('Please select a service');
      return;
    }
    if (!formData.scheduledDate || !formData.scheduledTime) {
      alert('Please select a date and time');
      return;
    }

    const baseGuestName = formData.guestName || profile?.full_name || '';
    const baseGuestEmail = formData.guestEmail || profile?.email || '';
    const baseGuestPhone = formData.guestPhone || profile?.phone || '';

    const draftBooking = {
      service_id: formData.serviceId,
      service_price: selectedService.price,
      scheduled_date: formData.scheduledDate,
      scheduled_time: formData.scheduledTime,
      location_type: formData.locationType,
      address: formData.locationType === 'mobile' ? formData.address : null,
      city: formData.locationType === 'mobile' ? formData.city : null,
      state: formData.locationType === 'mobile' ? formData.state : null,
      total_amount: selectedService.price + travelFee,
      travel_fee: travelFee,
      travel_miles: travelMiles,
      guest_name: baseGuestName,
      guest_email: baseGuestEmail,
      guest_phone: baseGuestPhone,
      notes: formData.notes || '',
      status: 'pending' as const,
      payment_status: 'pending' as const,
      user_id: profile?.id ?? null,
      add_ons: [] as any[]
    };

    try {
      sessionStorage.setItem('pendingBooking', JSON.stringify(draftBooking));
    } catch (err) {
      console.warn('Could not persist pending booking to sessionStorage:', err);
    }

    const nextState = {
      booking: draftBooking,
      meta: {
        service: selectedService,
        travelFee,
        travelMiles,
        addOns: []
      }
    };

    if (!profile) {
      console.log('User not authenticated, navigating to quick-auth');
      navigate('/quick-auth', { state: nextState });
      return;
    }

    setSubmitting(true);
    try {
      navigate('/add-ons', { state: nextState });
    } finally {
      setSubmitting(false);
    }
  };

  // --- JSX Rendering ---
  if (loading) {
    return (
      <div className="booking-loading">
        <div className="loading-spinner"></div>
        <p>Loading booking form...</p>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <div className="container">
        <div className="booking-header">
          <h1 className="booking-title">Book Your Service</h1>
          <p className="booking-description">
            Schedule your car detailing appointment in just a few simple steps.
          </p>
        </div>
        <div className="booking-content">
          <div className="booking-form-container">
            <form className="booking-form" onSubmit={handleSubmit}>
              {/* Service Selection */}
              <div className="form-section">
                <h3 className="section-title">Select Service</h3>
                <select
                  name="serviceId"
                  value={formData.serviceId}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">Choose a service...</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - ${service.price}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location Selection */}
              <div className="form-section">
                <h3 className="section-title">Service Location</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Choose location:</label>
                    <select
                      name="locationType"
                      value={formData.locationType}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="dropoff">You come to me (drop off at business)</option>
                      <option value="mobile">I'll come to you (mobile service)</option>
                    </select>
                  </div>
                  {formData.locationType === 'dropoff' && (
                    <div className="form-group">
                      <label className="form-label">Business Address:</label>
                      <div className="form-static">
                        {businessAddress}
                      </div>
                    </div>
                  )}
                  {formData.locationType === 'mobile' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="address" className="form-label">Street Address *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="city" className="form-label">City *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="state" className="form-label">State *</label>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </>
                  )}
                </div>
                {formData.locationType === 'mobile' && (
                  <div className="form-group">
                    <label className="form-label">Travel Fee:</label>
                    <div className="form-static">${travelFee.toFixed(2)} (first 15 miles free, $5/mile after, capped at $40)</div>
                  </div>
                )}
              </div>

              {/* Total Price */}
              <div className="form-section">
                <h3 className="section-title">Total Price</h3>
                <div className="form-price-breakdown">
                  <div className="price-row">
                    <span>Service price</span>
                    <span>${selectedService ? selectedService.price.toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="price-row">
                    <span>Travel fee</span>
                    <span>${travelFee.toFixed(2)}</span>
                  </div>
                  <div className="price-divider" />
                  <div className="price-row total">
                    <span>Total due at booking</span>
                    <span>${selectedService ? (selectedService.price + travelFee).toFixed(2) : '0.00'}</span>
                  </div>
                </div>
                {formData.locationType === 'mobile' && (
                  <p className="section-subtext">
                    Distance from shop: {travelMiles.toFixed(1)} miles (first 15 miles free, $5/mile after, capped at $40)
                  </p>
                )}
              </div>

              {/* Date & Time Selection (assuming Calendar and TimeSlotSelector components exist) */}
              <div className="form-section">
                <h3 className="section-title">Select Date & Time</h3>
                <div className="datetime-selection">
                  <Calendar
                    selectedDate={formData.scheduledDate}
                    onDateSelect={handleDateSelect}
                    minDate={today}
                    maxDate={maxSelectableDate}
                    disabledDates={fullyBookedDates}
                    bookedDates={bookedDateList}
                  />
                  {availabilityError && (
                    <div className="availability-message error">
                      We couldn't load existing bookings. Slots might be inaccurate until you refresh.
                    </div>
                  )}
                  {formData.scheduledDate && !selectedService && (
                    <div className="availability-message">
                      Choose a service to see accurate availability for this date.
                    </div>
                  )}
                  {formData.scheduledDate && selectedService && (
                    <TimeSlotSelector
                      selectedTime={formData.scheduledTime}
                      onTimeSelect={handleTimeSelect}
                      availableSlots={availableSlots}
                      existingBookings={selectedDateBookings}
                      loading={availabilityLoading}
                    />
                  )}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="form-section">
                <h3 className="section-title">Additional Notes</h3>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows={4}
                  placeholder="Any special requests or instructions for your service..."
                />
              </div>

              {/* Proceed to payment button (replaces previous "Book Appointment") */}
              <button
                type="submit"
                className="submit-button"
                disabled={submitting || !formData.serviceId || !formData.scheduledDate || !formData.scheduledTime}
              >
                {submitting ? 'Processing...' : 'Proceed to payment'}
              </button>
            </form>
          </div>

          {selectedService && (
            <div className="booking-summary">
              <h3 className="summary-title">Booking Summary</h3>
              <div className="summary-content">
                <div className="summary-item">
                  <span className="summary-label">Service</span>
                  <span className="summary-value">{selectedService.name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Duration</span>
                  <span className="summary-value">
                    Estimated {selectedService.duration} hour(s) max
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Price</span>
                  <span className="summary-value price">${selectedService.price}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Travel Fee</span>
                  <span className="summary-value">${travelFee.toFixed(2)}</span>
                </div>
                <div className="summary-item total">
                  <span className="summary-label">Est. Total</span>
                  <span className="summary-value">${(selectedService.price + travelFee).toFixed(2)}</span>
                </div>
                {formData.scheduledDate && (
                  <div className="summary-item">
                    <span className="summary-label">Date</span>
                    <span className="summary-value">
                      {new Date(formData.scheduledDate + 'T00:00:00').toLocaleDateString()}
                    </span>
                  </div>
                )}
                {formData.scheduledTime && (
                  <div className="summary-item">
                    <span className="summary-label">Time</span>
                    <span className="summary-value">{formData.scheduledTime}</span>
                  </div>
                )}
                <div className="summary-item">
                  <span className="summary-label">Location</span>
                  <span className="summary-value">
                    {formData.locationType === 'mobile'
                      ? `${formData.address || 'Address pending'}, ${formData.city || ''} ${formData.state || ''}`
                      : businessAddress}
                  </span>
                </div>
                {formData.locationType === 'mobile' && (
                  <div className="summary-item">
                    <span className="summary-label">Travel Distance</span>
                    <span className="summary-value">{travelMiles.toFixed(1)} miles</span>
                  </div>
                )}
              </div>
              <div className="summary-note">
                <p>⚡ We'll contact you within 24 hours to confirm your appointment</p>
                <p>🕑 Need help sooner? Call or text <a href="tel:19044871591">904-487-1591</a>.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;